import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@babackapp/ui/components/dialog";
import { ArrowTopRightIcon, ExternalLinkIcon } from "@radix-ui/react-icons";
import { animated, config, useTrail } from "@react-spring/web";
import {
	type PropsWithChildren,
	useCallback,
	useEffect,
	useState,
} from "react";
import IntegrationLogos from "./integration-logos";

type IntegrationDialogProps = {
	logo?: string;
	title: string;
	description?: string;
	seeMoreUrl: string;
	onConfigure?: () => void;
	open?: boolean;
};

export default function IntegrationDialog({
	title,
	description,
	seeMoreUrl,
	logo,
	onConfigure,
	children,
	open: initialOpen,
}: PropsWithChildren<IntegrationDialogProps>) {
	const [dialogOpen, setDialogOpen] = useState(initialOpen || false);
	const t = useTranslation();

	const [[logosStyle, titleStyle, descriptionStyle, infoButtonStyle], api] =
		useTrail(4, () => ({
			opacity: 0,
			transform: "translateY(10px)",
			immediate: true,
			config: { ...config.gentle, duration: 250 },
			delay: 200,
		}));

	const onDialogOpenChange = useCallback(
		(open: boolean) => {
			setDialogOpen(open);
			if (open) {
				api.start({ opacity: 1, transform: "translateY(0)" });
			} else {
				api.start({ opacity: 0, transform: "translateY(10px)" });
			}
		},
		[api],
	);

	useEffect(() => {
		// When the component mounts opened, we want to start the animation
		if (initialOpen) {
			setDialogOpen(true);
			api.start({ opacity: 1, transform: "translateY(0)" });
		}
	}, [api, initialOpen]);

	return (
		<Dialog open={dialogOpen} onOpenChange={onDialogOpenChange}>
			<DialogTrigger asChild>
				<Button variant="secondary" onClick={onConfigure} className="gap-2">
					Configure
					<ArrowTopRightIcon />
				</Button>
			</DialogTrigger>
			<DialogContent>
				<DialogHeader className="flex flex-col pt-[24px] pb-[24px] px-[16px] rounded-md justify-center bg-muted mt-[16px]">
					<animated.div
						style={logosStyle}
						className="mb-[24px] flex items-center justify-center"
					>
						<IntegrationLogos logo={logo} active={dialogOpen} />
					</animated.div>
					<div className="justify-center items-center flex flex-col">
						<animated.div style={titleStyle}>
							<DialogTitle className="mt-[16px] text-center">
								{title}
							</DialogTitle>
						</animated.div>
						<animated.div style={descriptionStyle}>
							<DialogDescription className="mb-[16px] py-[8px] text-center">
								{description}
							</DialogDescription>
						</animated.div>
						<animated.div style={infoButtonStyle}>
							<a href={seeMoreUrl} target="_blank" rel="noreferrer">
								<Button
									variant="outline"
									className="gap-2 self-center"
									size="sm"
								>
									{t("misc.seemore")}
									<ExternalLinkIcon />
								</Button>
							</a>
						</animated.div>
					</div>
				</DialogHeader>
				{children}
			</DialogContent>
		</Dialog>
	);
}
